import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'warning',
    title: 'API Usage Alert',
    message: 'You have reached 80% of your daily API call limit',
    timestamp: '2024-01-20 15:30:00',
    read: false,
  },
  {
    id: '2',
    type: 'success',
    title: 'New API Key Generated',
    message: 'A new API key has been created for Project Alpha',
    timestamp: '2024-01-20 14:15:00',
    read: false,
  },
  {
    id: '3',
    type: 'error',
    title: 'Webhook Delivery Failed',
    message: 'Failed to deliver webhook to endpoint: Connection timeout',
    timestamp: '2024-01-20 12:45:00',
    read: true,
  },
];

export default function NotificationDropdown() {
  return (
    <Menu as="div" className="relative z-[100]">
      <Menu.Button className="p-2 text-white/60 hover:text-white hover:bg-white/[0.04] transition-colors duration-200 relative rounded-lg">
        <BellIcon className="h-6 w-6" />
        <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-magick-purple rounded-full" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="fixed right-6 top-[4.5rem] w-96 origin-top-right bg-[#1a1b2e]/95 backdrop-blur-lg rounded-lg divide-y divide-white/[0.05] shadow-xl ring-1 ring-white/[0.05] focus:outline-none isolate">
          <div className="px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-medium text-white">Notifications</h2>
            <Link
              to="/notifications"
              className="text-sm text-white/60 hover:text-white"
            >
              View All
            </Link>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {mockNotifications.map((notification) => (
              <Menu.Item key={notification.id}>
                {({ active }) => (
                  <div
                    className={`px-4 py-3 ${
                      active ? 'bg-white/[0.02]' : ''
                    } ${!notification.read ? 'bg-white/[0.01]' : ''}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`p-2 rounded ${
                          notification.type === 'success'
                            ? 'bg-emerald-500/10'
                            : notification.type === 'warning'
                            ? 'bg-amber-500/10'
                            : 'bg-rose-500/10'
                        }`}
                      >
                        {notification.type === 'success' ? (
                          <CheckCircleIcon className="h-5 w-5 text-emerald-300" />
                        ) : notification.type === 'warning' ? (
                          <ExclamationTriangleIcon className="h-5 w-5 text-amber-300" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-rose-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">
                          {notification.title}
                        </p>
                        <p className="text-sm text-white/70 truncate">
                          {notification.message}
                        </p>
                        <p className="text-xs text-white/40 mt-1">
                          {notification.timestamp}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Menu.Item>
            ))}
          </div>
          <div className="px-4 py-3">
            <Link
              to="/notifications"
              className="block w-full text-center text-sm text-white/60 hover:text-white"
            >
              See all notifications
            </Link>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
